import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { i18n } from '@/i18n';
import { DEFAULT_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT } from '@/customerForecastModule/constants';
import { IYearMonth } from '@/coreDomainsModule/date';
import {
  ICustomerForecastTimelineFilterContentItem,
  ICustomerForecastTimelineState,
} from '../domains/entities/customerForecastTimelineState';
import { CustomerForecastTimelineViewModel } from './customerForecastTimelineViewModel';
import { ICustomerForecastRepository } from '@/coreInfrastructureModule/interfaces/ICustomerForecastRepository';
import { BulkGetCustomerForecastTimelineUseCase } from '../useCases/bulkGetCustomerForecastTimelineUseCase';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { CustomerForecastTimelineFilterAdapter } from '../adapter/customerForecastTimelineFilterAdapter';
import { GetBaseBusinessIdUseCase } from '../useCases/getBaseBusinessIdUseCase';
import { GetCustomerForecastTimelineFilterItemsUseCase } from '../useCases/getCustomerForecastTimelineFilterItemsUseCase';

export const useCustomerForecastTimelineStore = defineStore('useCustomerForecastTimelineStore', () => {
  const t = i18n.t;
  const vm = new CustomerForecastTimelineViewModel();

  const state = reactive<ICustomerForecastTimelineState>({
    tableItems: undefined,
    targetStartYearMonth: {
      year: 0,
      month: 0,
    },
    displayForecastsMonth: DEFAULT_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT,
    filterContentsInfo: {
      filterItems: [],
    },
    tableLoadingState: {
      status: {
        type: 'none',
      },
    },
    fetchBaseBusinessIdLoadingState: {
      status: {
        type: 'none',
      },
    },
    isDisabledApplyFilterButton: false,
    targetYearMonthList: [],
    receivedYearMonthList: [],
    downloadModalState: {
      modalState: false,
      startYearMonth: {
        year: 0,
        month: 0,
      },
      endYearMonth: {
        year: 0,
        month: 0,
      },
    },
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.tableItems = undefined;
    state.targetStartYearMonth = {
      year: 0,
      month: 0,
    };
    state.displayForecastsMonth = DEFAULT_CUSTOMER_FORECAST_TIMELINE_MONTH_COUNT;
    state.filterContentsInfo = {
      filterItems: [],
      currentFilterType: undefined,
    };
    state.tableLoadingState = {
      status: {
        type: 'none',
      },
    };
    state.fetchBaseBusinessIdLoadingState = {
      status: {
        type: 'none',
      },
    };
    state.isDisabledApplyFilterButton = false;
    state.targetYearMonthList = [];
    state.receivedYearMonthList = [];
    state.openedSelectInputType = undefined;
    state.targetBaseBusinessId = undefined;
    state.downloadModalState = {
      modalState: false,
      startYearMonth: {
        year: 0,
        month: 0,
      },
      endYearMonth: {
        year: 0,
        month: 0,
      },
    };
  };

  /**
   * 初期化処理
   * @param repository - リポジトリ
   * @param targetBusinessId - 対象の取引ID
   */
  const init = async (repository: ICustomerForecastRepository, targetBusinessId?: string) => {
    $reset();
    // 年月リストを更新
    state.targetStartYearMonth = vm.calculateTargetStartYearMonthFromMonths(state.displayForecastsMonth);
    updateYearMonth(state.targetStartYearMonth, state.displayForecastsMonth);

    state.targetBaseBusinessId = targetBusinessId;
    // フィルターの初期化
    await initFilterContents(repository, state.targetBaseBusinessId);
    // フィルターの適用ボタンの非活性化状態を更新
    updateIsDisabledApplyFilterButton(state.filterContentsInfo.filterItems);
    // 表示アイテムの取得
    await updateDisplayData(repository);
  };

  /**
   * フィルター項目の初期化
   * @param repository - リポジトリ
   * @param targetBusinessId - 対象の取引ID
   */
  const initFilterContents = async (repository: ICustomerForecastRepository, targetBusinessId?: string) => {
    // フィルター項目の初期化
    state.filterContentsInfo = {
      filterItems: vm.clearFilterContents(),
      currentFilterType: undefined,
    };

    if (targetBusinessId !== undefined && targetBusinessId !== '') {
      // フィルターの初期値を設定
      await getFilterItemsByBusinessId(repository, targetBusinessId);
    }
  };

  /**
   * 取引IDに紐づくフィルター項目を取得
   * @param repository - リポジトリ
   * @param businessId - 取引ID
   */
  const getFilterItemsByBusinessId = async (repository: ICustomerForecastRepository, businessId: string) => {
    // ローディング開始
    state.fetchBaseBusinessIdLoadingState.status.type = 'loading';

    const useCase = new GetCustomerForecastTimelineFilterItemsUseCase();
    const result = await useCase.Execute(repository, businessId);

    switch (result.status.type) {
      case 'success':
        if (result.status.data === undefined) {
          // ローディング終了
          state.fetchBaseBusinessIdLoadingState.status.type = 'zeroMatch';
          // エラートーストを表示
          toast.error(t('該当する取引情報が見つかりませんでした。'));
          return;
        }

        // ローディング終了
        state.fetchBaseBusinessIdLoadingState.status.type = 'success';
        // 取得したデータをstateにセット
        state.filterContentsInfo.filterItems = vm.getSetFilterContents(result.status.data);
        break;
      default:
        // ローディング終了
        state.fetchBaseBusinessIdLoadingState.status.type = 'error';
        // エラートーストを表示
        toast.error(t('取引情報の取得に失敗しました。'));
        break;
    }
  };

  /**
   * 内示遍歴情報の取得
   * @param repository - リポジトリ
   */
  const updateDisplayData = async (repository: ICustomerForecastRepository) => {
    if (state.targetBaseBusinessId === undefined || state.targetBaseBusinessId === '') return;
    // 内示遍歴情報の取得
    await getCustomerForecastTimelineItems(repository, state.targetBaseBusinessId, state.targetStartYearMonth, state.displayForecastsMonth);
  };

  /**
   * 内示遍歴情報の取得
   * @param repository - リポジトリ
   * @param baseBusinessId - 対象の取引ID
   * @param targetStartYearMonth - 開始年月
   * @param displayForecastsMonth - 表示する内示の月数
   */
  const getCustomerForecastTimelineItems = async (
    repository: ICustomerForecastRepository,
    baseBusinessId: string,
    targetStartYearMonth: IYearMonth,
    displayForecastsMonth: number
  ) => {
    // ローディング開始
    state.tableLoadingState.status.type = 'loading';

    const useCase = new BulkGetCustomerForecastTimelineUseCase();
    const result = await useCase.Execute(repository, baseBusinessId, targetStartYearMonth, displayForecastsMonth);

    switch (result.status.type) {
      case 'success':
        // ゼロマッチ判定
        if (result.status.data.customerForecasts.length === 0 && result.status.data.salesResults.length === 0) {
          state.tableLoadingState.status.type = 'zeroMatch';
          return;
        }
        // ローディング終了
        state.tableLoadingState.status.type = 'success';
        // 取得したデータをstateにセット
        state.tableItems = result.status.data;
        break;
      default:
        // ローディング終了
        state.tableLoadingState.status.type = 'error';
        break;
    }
  };

  /**
   * 内示遍歴の更新
   * @param targetStartYearMonth - 開始年月
   * @param displayForecastsMonth - 表示する内示の月数
   */
  const updateYearMonth = (targetStartYearMonth: IYearMonth, displayForecastsMonth: number) => {
    // 年月リストを更新
    state.targetYearMonthList = vm.getTargetYearMonthList(targetStartYearMonth, displayForecastsMonth);
    state.receivedYearMonthList = vm.getReceivedYearMonthList(targetStartYearMonth, displayForecastsMonth);
  };

  /**
   * フィルターの適用ボタンの非活性化状態を更新
   * @param filterContentsInfos - フィルターの内容情報
   */
  const updateIsDisabledApplyFilterButton = (filterContentsInfos: ICustomerForecastTimelineFilterContentItem[]) => {
    state.isDisabledApplyFilterButton = vm.getIsDisabledApplyFilterButton(filterContentsInfos);
  };

  /**
   * 表示月数を取得
   */
  const getMonthsDisplayForecast = () => {
    return [...vm.MONTH_OPTIONS_DISPLAY_FORECAST].map((monthsOption) => monthsOption + t('unitMonth'));
  };

  /**
   * 開始年月を選択した際の処理
   * @param selectedYearMonth - 選択された年月
   * @param repository - 取引情報のリポジトリ
   */
  const onSelectedStartYearMonth = async (selectedYearMonth: IYearMonth, repository: ICustomerForecastRepository) => {
    if (vm.isDisabledTargetStartYearMonth(selectedYearMonth)) {
      // 無効な範囲を選択した場合、エラートーストを表示
      toast.warning(t('24ヶ月前から1ヶ月先までの範囲で選択してください'));
      state.targetStartYearMonth = state.targetStartYearMonth;
      return;
    }

    state.targetStartYearMonth = selectedYearMonth;
    // 年月リストを更新
    updateYearMonth(state.targetStartYearMonth, state.displayForecastsMonth);
    // 表示アイテムの取得
    await updateDisplayData(repository);
  };

  /**
   * 表示する内示の月数を選択した際の処理
   * @param selectedIndex - 選択肢のインデックス
   * @param repository - 取引情報のリポジトリ
   */
  const onSelectedDisplayForecastsMonth = async (selectedIndex: number, repository: ICustomerForecastRepository) => {
    if (selectedIndex === undefined) return;
    // 選択肢のindexから表示する内示の月数を取得してセット
    state.displayForecastsMonth = vm.getMonthDisplayForecastByIndex(selectedIndex);
    // 年月リストを更新
    updateYearMonth(state.targetStartYearMonth, state.displayForecastsMonth);
    // 表示アイテムの取得
    await updateDisplayData(repository);
  };

  /**
   * フィルタに指定するプロパティを取得
   */
  const getFilterProperty = () => {
    return CustomerForecastTimelineFilterAdapter.adapt(state.filterContentsInfo.filterItems);
  };

  /**
   * フィルター適用ボタンをクリックした際の処理
   * @param repository - 取引情報のリポジトリ
   */
  const onClickApplyFilterButton = async (repository: ICustomerForecastRepository) => {
    // 関連取引IDを取得
    await getBaseBusinessId(repository);
    if (state.fetchBaseBusinessIdLoadingState.status.type !== 'success') return;

    // 表示アイテムの取得
    await updateDisplayData(repository);
  };

  /**
   * 関連取引IDを取得
   * @param repository - 取引情報のリポジトリ
   */
  const getBaseBusinessId = async (repository: ICustomerForecastRepository) => {
    // ローディング開始
    state.fetchBaseBusinessIdLoadingState.status.type = 'loading';

    const useCase = new GetBaseBusinessIdUseCase();
    const result = await useCase.Execute(repository, getFilterProperty());

    switch (result.status.type) {
      case 'success':
        if (result.status.data === undefined || result.status.data === '') {
          // ローディング終了
          state.fetchBaseBusinessIdLoadingState.status.type = 'zeroMatch';
          // エラートーストを表示
          toast.error(t('該当する取引情報が見つかりませんでした。'));
          return;
        }

        // ローディング終了
        state.fetchBaseBusinessIdLoadingState.status.type = 'success';
        // 取得したデータをstateにセット
        state.targetBaseBusinessId = result.status.data;
        break;
      default:
        // ローディング終了
        state.fetchBaseBusinessIdLoadingState.status.type = 'error';
        // エラートーストを表示
        toast.error(t('取引情報の取得に失敗しました。'));
        break;
    }
  };

  /**
   * フィルターのクリアボタンをクリックした際の処理
   */
  const onClickClearFilterButton = () => {
    state.filterContentsInfo.filterItems = vm.clearFilterContents();
    updateIsDisabledApplyFilterButton(state.filterContentsInfo.filterItems);
  };

  /**
   * 内示遍歴ダウンロードモーダルの開始年月を選択した際の処理
   * @param selectedYearMonth - 選択された年月
   */
  const onSelectedStartYearMonthDownloadModal = async (selectedYearMonth: IYearMonth) => {
    if (vm.isDisabledStartYearMonthDownloadModal(selectedYearMonth)) {
      // 無効な範囲を選択した場合、エラートーストを表示
      toast.warning(t('10年前から2年後までの範囲で選択してください'));
      state.downloadModalState.startYearMonth = state.downloadModalState.startYearMonth;
      return;
    }

    state.downloadModalState.startYearMonth = selectedYearMonth;
  };

  /**
   * 内示遍歴ダウンロードモーダルの終了年月を選択した際の処理
   * @param selectedYearMonth - 選択された年月
   */
  const onSelectedEndYearMonthDownloadModal = async (selectedYearMonth: IYearMonth) => {
    if (vm.isDisabledEndYearMonthDownloadModal(selectedYearMonth)) {
      // 無効な範囲を選択した場合、エラートーストを表示
      toast.warning(t('10年前から2年後までの範囲で選択してください'));
      state.downloadModalState.endYearMonth = state.downloadModalState.endYearMonth;
      return;
    }

    state.downloadModalState.endYearMonth = selectedYearMonth;
  };

  /**
   * 内示遍歴ダウンロードモーダルの適用ボタンをクリックした際の処理
   */
  const onClickDownloadApplyButton = async () => {
    // TODO: ダウンロード処理
    // モーダルを閉じる
    state.downloadModalState.modalState = false;
    // TODO: ダウンロード処理用のローディングを用意。
    // TODO: まずtableをcssデータに変換。
    // TODO: cssデータをエクセルファイルに変換。
    // TODO: エクセルファイルをダウンロード。
    // TODO: ダウンロード処理が完了したら、ローディングを終了。トーストを表示。
  };

  return {
    state,
    $reset,
    init,
    updateDisplayData,
    getCustomerForecastTimelineItems,
    updateYearMonth,
    updateIsDisabledApplyFilterButton,
    getMonthsDisplayForecast,
    onSelectedStartYearMonth,
    onSelectedDisplayForecastsMonth,
    getFilterProperty,
    onClickApplyFilterButton,
    getBaseBusinessId,
    onClickClearFilterButton,
    onSelectedStartYearMonthDownloadModal,
    onSelectedEndYearMonthDownloadModal,
    onClickDownloadApplyButton,
  };
});

/**
 * storeの型定義
 */
export type CustomerForecastTimelineStore = ReturnType<typeof useCustomerForecastTimelineStore>;
